import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableFooter,
    TablePagination,
    TextField,
    Typography,
} from '@material-ui/core';
import {useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useQuery} from '@tanstack/react-query';
import {Contact, Search} from 'lucide-react';
import {useDebouncedCallback} from 'use-debounce';
import AthenaAPI from '@/APIs/AthenaAPI';
import {LoadingSpinner} from '@/components/ui/loading-spinner';
import {useQueryParams} from '@/lib/hooks/use-query-params';
import {OneContact} from './OneContact';

const EmtpyState = () => (
    <div className="flex flex-col items-center justify-center gap-2 pt-28 pb-4">
        <Contact className="h-20 w-20 text-primary-200" />
        <h2 className="text-3xl text-black/60">No contacts found.</h2>
    </div>
);

const LoadingState = () => (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 pt-28 pb-4">
        <LoadingSpinner className="w-10 h-10" />
        <h2 className="text-muted-foreground">Loading contacts...</h2>
    </div>
);

export const ContactsPanel = observer(({selectContact}: {selectContact: null | ((email: string) => void)}) => {
    const params = useQueryParams();
    const search = params.get('q') || '';
    const page = Number(params.get('page')) || 1;
    const limit = Number(params.get('limit')) || 10;
    const tableRef = useRef<HTMLDivElement>(null);

    const [_search, setSearch] = useState(search);

    const {isLoading, data, isFetching} = useQuery({
        queryKey: ['contacts', search, page, limit],
        queryFn: async () => {
            const resp = await AthenaAPI.getContacts({search, page, limit});

            if (resp && resp?.success) {
                return resp;
            }

            console.error('Failed to fetch contacts', resp);

            return {
                data: [],
                meta: {
                    total: 0,
                    limit,
                    currentPage: page,
                },
            };
        },
        keepPreviousData: true,
    });

    const onChangePage = (page: number) => {
        requestAnimationFrame(() => tableRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'}));
        params.set('page', String(page));
    };

    const onChangeRowsPerPage = (perPage: number) => {
        params.set('limit', String(perPage));
    };

    const onSearch = useDebouncedCallback(() => {
        params.set('q', _search);
    }, 300);

    const searchTextChanged = async (search: string) => {
        setSearch(search);
        onSearch();
    };

    return (
        <div ref={tableRef} className="flex flex-col p-4 h-full">
            <div className="flex flex-col mb-4 gap-y-4 sm:flex-row sm:items-center">
                <Typography variant="h6" className="text-primary !text-3xl">
                    Clients
                </Typography>
                <TextField
                    id="standard-basic"
                    label="Search Clients"
                    className="!ml-0 !w-full bg-white sm:!ml-auto sm:!w-96"
                    value={_search}
                    onChange={(e) => {
                        searchTextChanged(e.target.value);
                    }}
                    variant="outlined"
                    data-testid="search-client"
                    InputProps={{
                        startAdornment: isFetching ? (
                            <LoadingSpinner className="w-6 h-6 mr-2" />
                        ) : (
                            <Search className="mr-2 text-primary-300" />
                        ),
                    }}
                />
            </div>

            {data?.data?.length ? (
                <TableContainer className="contacts-list" component={Paper}>
                    <Table style={{minWidth: '100%'}} data-testid="contact-list">
                        <TableHead>
                            <TableRow className="[&>th]:font-bold">
                                <TableCell>First Name</TableCell>
                                <TableCell>Last Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Create Date</TableCell>
                                <TableCell>Owner</TableCell>
                                <TableCell>Cases</TableCell>
                                {selectContact != null ? <TableCell>Control</TableCell> : null}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data?.data?.map((contact) => (
                                <OneContact key={contact.id} selectContact={selectContact} contact={contact} />
                            ))}
                        </TableBody>

                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    count={data?.meta.total}
                                    rowsPerPage={limit}
                                    page={page - 1}
                                    onChangePage={(_e, page) => onChangePage(page + 1)}
                                    onChangeRowsPerPage={(e) => onChangeRowsPerPage(Number(e.target.value))}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            ) : isLoading ? (
                <LoadingState />
            ) : (
                <EmtpyState />
            )}
        </div>
    );
});
